import { VALIDATE_UTILS } from '@widergy/web-utils';
import {
  normalizeDate,
  withoutNumbers,
  normalizeName,
  normalizeUpperCase,
  normalizeAll,
  removeWhitespaces,
  normalizeLowerCase,
  numberMaxLength
} from '@widergy/web-utils/lib/normalize';
import {
  required,
  email,
  passwordValidations,
  lengthEquals,
  pattern,
  numericalFourDigits
} from '@widergy/web-utils/lib/validate';
import moment from 'moment';

import { normalizeAccount } from 'utils/formatUtils';
import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';

import { CARRIERS } from './common';
import {
  FIELD_SELECT,
  FIELD_TEXT,
  FIELD_PHONE,
  FIELD_EMAIL,
  FIELD_PASSWORD,
  FIELD_CAPTCHA
} from './userForms';

const fieldsConfig = appConfig.signUp.personalInformationFields;
const NAME_MAX_LENGTH = appConfig.signUp.nameMaxLength;
const LAST_NAME_MAX_LENGTH = appConfig.signUp.lastNameMaxLength;
const MAX_DATE = moment()
  .subtract('18', 'years')
  .format('DD-MM-YYYY');
const MIN_DATE = moment()
  .subtract('100', 'years')
  .format('DD-MM-YYYY');

export const PASSWORD_MESSAGES = {
  passwordRequired: message('Signup:passwordRequired'),
  passwordExactLength: message('Signup:passwordExactLength', {
    exactLength: appConfig.passwordMinLength
  }),
  passwordMinLength: message('Signup:passwordMinLength', { minLength: appConfig.passwordMinLength }),
  passwordMaxLength: message('Signup:passwordMaxLength', { maxLength: appConfig.passwordMaxLength }),
  passwordRequireLetterAndNumber: message('Validations:passwordRequireLetterAndNumber'),
  passwordStrength: message('Validations:invalidFormat')
};

const passwordValidation = passwordValidations(
  appConfig.passwordMinLength,
  appConfig.passwordMaxLength,
  appConfig.passwordRequireNumberAndLetter,
  false,
  PASSWORD_MESSAGES
);

const platformDiscoveryOptions = [
  {
    id: 0,
    key: message('Signup:platformDiscovery1'),
    value: 'phone',
    name: message('Signup:platformDiscovery1'),
    placeholder: message(' Signup:platformDiscovery1'),
    order: 0
  },
  {
    id: 1,
    key: message('Signup:platformDiscovery2'),
    value: 'email',
    name: message('Signup:platformDiscovery2'),
    placeholder: message('Signup:platformDiscovery2'),
    order: 1
  },
  {
    id: 2,
    key: message('Signup:platformDiscovery3'),
    value: 'web',
    name: message('Signup:platformDiscovery3'),
    placeholder: message('Signup:platformDiscovery3'),
    order: 2
  },
  {
    id: 3,
    key: message('Signup:platformDiscovery4'),
    value: 'referred',
    name: message('Signup:platformDiscovery4'),
    order: 3
  },
  {
    id: 4,
    key: message('Signup:platformDiscovery5'),
    value: 'other',
    name: message('Signup:platformDiscovery5'),
    placeholder: message('Signup:platformDiscovery5'),
    order: 4
  }
];

export const docTypeOptions = [
  {
    id: 0,
    key: 'dni',
    value: 'dni',
    name: message('Signin:dni'),
    placeholder: message('Signin:dni'),
    order: 0
  },
  {
    id: 1,
    key: 'cuit',
    value: 'cuit',
    name: message('Signin:cuit'),
    placeholder: message('Signin:cuit'),
    order: 1
  },
  {
    id: 2,
    key: 'provincial_ci',
    value: 'provincial_ci',
    name: message('Signin:cip'),
    placeholder: message('Signin:cip'),
    order: 2
  },
  {
    id: 3,
    key: 'identity_card',
    value: 'identity_card',
    name: message('Signin:cdi'),
    placeholder: message('Signin:cdi'),
    order: 3
  },
  {
    id: 4,
    key: 'enlistment_book',
    value: 'enlistment_book',
    name: message('Signin:le'),
    placeholder: message('Signin:le'),
    order: 4
  },
  {
    id: 6,
    key: 'passport',
    value: 'passport',
    name: message('Signin:passport'),
    placeholder: message('Signin:passport'),
    order: 6
  },
  {
    id: 7,
    key: 'civic_notebook',
    value: 'civic_notebook',
    name: message('Signin:lc'),
    placeholder: message('Signin:lc'),
    order: 7
  }
];

export const documentTypeField = {
  id: 1,
  type: FIELD_SELECT,
  key: 'document_type',
  required: fieldsConfig.document_type.required,
  placeholder: message('Signup:documentType'),
  field_options: docTypeOptions,
  validate: [required(message('Validations:required'))]
};

export const documentValidation = () => (value, allValues) => {
  const type = allValues.docType || allValues.document_type || allValues[`id${documentTypeField.id}`]; // In case of mapping fields by id.
  switch (type) {
    case 'dni':
      return VALIDATE_UTILS.validateMinLength(value, 6) && VALIDATE_UTILS.validateMaxLength(value, 8)
        ? undefined
        : message('Signin:dniFormat');
    case 'cuit':
      return VALIDATE_UTILS.validateEquals(value, 11) && VALIDATE_UTILS.meetCuitRequirements(value)
        ? undefined
        : message('Signin:cuitFormat');
    case 'other':
      return VALIDATE_UTILS.validateMinLength(value, 5) && VALIDATE_UTILS.validateMaxLength(value, 11)
        ? undefined
        : message('Signin:otherFormat');
    case 'provincial_ci':
      return VALIDATE_UTILS.validateMinLength(value, 7) && VALIDATE_UTILS.validateMaxLength(value, 8)
        ? undefined
        : message('Signin:cipFormat');
    case 'identity_card':
      return VALIDATE_UTILS.validateMinLength(value, 7) && VALIDATE_UTILS.validateMaxLength(value, 8)
        ? undefined
        : message('Signin:cdiFormat');
    case 'enlistment_book':
      return VALIDATE_UTILS.validateMinLength(value, 6) && VALIDATE_UTILS.validateMaxLength(value, 8)
        ? undefined
        : message('Signin:leFormat');
    case 'cert_of_document_in_proccess':
      return VALIDATE_UTILS.validateMinLength(value, 5) && VALIDATE_UTILS.validateMaxLength(value, 11)
        ? undefined
        : message('Signin:dniFormat');
    case 'passport':
      return VALIDATE_UTILS.validateMinLength(value, 7) && VALIDATE_UTILS.validateMaxLength(value, 9)
        ? undefined
        : message('Signin:passportFormat');
    case 'civic_notebook':
      return VALIDATE_UTILS.validateMinLength(value, 6) && VALIDATE_UTILS.validateMaxLength(value, 8)
        ? undefined
        : message('Signin:lcFormat');
    default:
      return value ? undefined : message('Signin:dniFormat');
  }
};

export const docTypeSignInOptions = [
  ...docTypeOptions,
  {
    id: 8,
    key: 'other',
    value: 'other',
    name: message('Signin:other'),
    placeholder: message('Signin:other'),
    order: 8
  }
];

export const platformDiscoveryField = {
  id: 12,
  type: FIELD_SELECT,
  key: 'platform_discovery',
  required: fieldsConfig.platform_discovery.required,
  placeholder: message('Signup:platformDiscovery0'),
  field_options: platformDiscoveryOptions,
  validate: []
};

export const documentNumberField = {
  id: 2,
  type: FIELD_TEXT,
  key: 'document_number',
  required: fieldsConfig.document_number.required,
  title: message('Signup:documentNumber'),
  validate: [VALIDATE_UTILS.minLength(5, message('Validations:invalidFormat')), documentValidation()],
  normalize: numberMaxLength(11)
};

export const nameField = {
  id: 3,
  type: FIELD_TEXT,
  key: 'name',
  required: fieldsConfig.name.required,
  title: message('Signup:name'),
  validate: [
    VALIDATE_UTILS.lettersAndSpaces(message('Validations:invalidFormat')),
    VALIDATE_UTILS.maxLength(NAME_MAX_LENGTH, message('Signup:nameMaxLength'))
  ],
  normalize: normalizeAll([normalizeName, normalizeUpperCase])
};

export const lastNameField = {
  id: 4,
  type: FIELD_TEXT,
  key: 'last_name',
  required: fieldsConfig.last_name.required,
  title: message('Signup:lastName'),
  validate: [
    VALIDATE_UTILS.lettersAndSpaces(message('Validations:invalidFormat')),
    VALIDATE_UTILS.maxLength(LAST_NAME_MAX_LENGTH, message('Signup:lastNameMaxLength'))
  ],
  normalize: normalizeAll([normalizeName, normalizeUpperCase])
};

export const contactFirstNameField = {
  id: 5,
  type: FIELD_TEXT,
  key: 'contact_first_name',
  required: fieldsConfig.contact_first_name.required,
  title: message('Signup:contactName'),
  validate: [VALIDATE_UTILS.lettersAndSpaces(message('Validations:invalidFormat'))],
  normalize: withoutNumbers,
  visibility_parent_field_key_id: 1,
  visibility_parent_field_option_values: [1]
};

export const contactLastNameField = {
  id: 6,
  type: FIELD_TEXT,
  key: 'contact_last_name',
  required: fieldsConfig.contact_last_name.required,
  title: message('Signup:contactLastName'),
  validate: [VALIDATE_UTILS.lettersAndSpaces(message('Validations:invalidFormat'))],
  normalize: withoutNumbers,
  visibility_parent_field_key_id: 1,
  visibility_parent_field_option_values: [1]
};

export const genderField = {
  id: 7,
  type: FIELD_SELECT,
  key: 'gender',
  required: fieldsConfig.gender.required,
  placeholder: message('Signup:gender'),
  field_options: [
    { id: '1', value: 'male', name: message('Signup:male'), order: '1' },
    { id: '2', value: 'female', name: message('Signup:female'), order: '2' }
  ],
  visibility_parent_field_key_id: 1,
  visibility_parent_field_option_values: [0, 2, 4, 5, 6, 7]
};

export const bornDateField = {
  id: 8,
  type: FIELD_TEXT,
  required: fieldsConfig.bornDate.required,
  key: 'bornDate',
  title: message('Signup:bornDate'),
  placeholder: message('Signup:bornDatePlaceholder'),
  validate: [
    VALIDATE_UTILS.lengthEquals(10, message('Validations:invalidFormat')),
    VALIDATE_UTILS.validDate(message('Validations:invalidFormat')),
    VALIDATE_UTILS.maxDate(MAX_DATE, message('Signup:maxDateError')),
    VALIDATE_UTILS.minDate(MIN_DATE, message('Signup:minDateError'))
  ],
  normalize: normalizeDate
};

export const carrierField = {
  id: 9,
  type: FIELD_SELECT,
  key: 'carrier',
  required: fieldsConfig.carrier.required,
  title: message('Signup:carrier'),
  placeholder: message('Signup:carrierPlaceholder'),
  field_options: CARRIERS,
  validate: appConfig.signUp.personalInformationFields.carrier.requiredOn
    ? [VALIDATE_UTILS.validateRequiredOnField('id10', message('Signup:carrierNeeded'))]
    : []
};

export const mobileField = {
  id: 10,
  type: FIELD_PHONE,
  key: 'cellNumber',
  required: fieldsConfig.cellNumber.required,
  title: message('Signup:cellNumber'),
  placeholder: message('Signup:cellNumberPlaceholder'),
  configuration: {
    area_code_label: message('PhoneInput:areaCodeLabel'),
    area_code_placeholder: message('PhoneInput:areaCodePlaceholder')
  },
  validate: appConfig.signUp.personalInformationFields.cellNumber.dependsOf && [
    VALIDATE_UTILS.validateDependantField('id11', message('Signup:onePhoneNeeded'))
  ]
};

export const phoneField = {
  id: 11,
  type: FIELD_PHONE,
  key: 'phone',
  required: fieldsConfig.phone.required,
  title: message('Signup:phone'),
  placeholder: message('Signup:phonePlaceholder'),
  configuration: {
    area_code_label: message('PhoneInput:areaCodeLabel'),
    area_code_placeholder: message('PhoneInput:areaCodePlaceholder')
  },
  validate: appConfig.signUp.personalInformationFields.phone.dependsOf && [
    VALIDATE_UTILS.validateDependantField('id10', message('Signup:onePhoneNeeded'))
  ]
};

export const emailField = {
  id: 12,
  type: FIELD_EMAIL,
  key: 'email',
  title: message('Commons:email'),
  validate: [required(message('Signin:emailRequired')), email(message('Validations:invalidEmail'))],
  normalize: normalizeAll([normalizeLowerCase, removeWhitespaces])
};

export const passwordField = {
  id: 13,
  type: FIELD_PASSWORD,
  key: 'password',
  title: message('Signin:password'),
  validate: passwordValidation
};

export const captchaField = {
  id: 14,
  type: FIELD_CAPTCHA,
  key: 'captcha',
  validate: [required(message('Translation:fieldRequired'))]
};

export const paymentDocumentType = docTypeOptions.filter(
  element => element.key === 'dni' || element.key === 'cuit'
);

export const clientNumberField = {
  id: 15,
  type: FIELD_TEXT,
  key: 'clientNumber',
  title: message('Accounts:clientNumberInput', { accountLength: appConfig.ACCOUNT_LENGTH }),
  placeholder: message('Accounts:clientNumberPlaceholder', { accountLength: appConfig.ACCOUNT_LENGTH }),
  validate: [
    required(message('Accounts:accountRequired')),
    lengthEquals(
      appConfig.ACCOUNT_LENGTH,
      message('Accounts:lengthValidation', {
        count: appConfig.ACCOUNT_LENGTH
      })
    ),
    pattern(appConfig.clientNumberRegex, message('Validations:invalidFormat'))
  ],
  normalize: normalizeAccount,
  tooltip: message('Accounts:tooltip')
};

export const codeField = {
  id: 16,
  type: FIELD_TEXT,
  key: 'code',
  title: message('Signup:verificationCode'),
  validate: [
    required(message('Validations:required')),
    numericalFourDigits(message('Validations:maxFourDigits'))
  ],
  normalize: numberMaxLength(4)
};
